import * as React from "react";
import {
  Link,
  createFileRoute,
  notFound, stripSearchParams 
} from "@tanstack/react-router";
import * as z from "zod";
import { zodValidator } from "@tanstack/zod-adapter";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { PlusIcon, TrashIcon } from "lucide-react";
import { HTTPError } from "ky";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { ColumnDef } from "@tanstack/react-table";
import type { Permission } from "@/lib/queries/permissions";
import type { RolePermissionAssociation } from "@/lib/queries/roles";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNextButton,
  PaginationPreviousButton,
} from "@/components/ui/pagination";
import { useAllPermissions } from "@/lib/queries/permissions";
import {
  rolePermissionsListQueryOptions,
  roleQueryOptions,
  useRolePermissionsList,
} from "@/lib/queries/roles";
import { fuzzyFilter } from "@/lib/table/fuzzyFilter";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/http";
import { useSet } from "@/lib/hooks";
import {
  GenericPaginationLinks,
  GenericTable,
} from "@/components/generic-table";
import { BreadcrumbNav } from "@/components/ui/breadcrumb";

const searchSchema = z.object({
  page: z.number().int().positive().default(1),
  per_page: z.number().int().positive().max(100).default(10),
  search: z.string().optional(),
});

const pathParamsSchema = z.object({
  id: z.coerce.number().int().positive(),
});

export const Route = createFileRoute("/_app/roles_/$id/permissions")({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  params: {
    parse: pathParamsSchema.parse,
  },

  search: {
    middlewares: [stripSearchParams({ search: undefined })],
  },
  loaderDeps: ({ search }) => ({
    page: search.page,
    per_page: search.per_page,
    search: search.search,
  }),

  async loader({ context: { queryClient }, deps, params }) {
    queryClient.ensureQueryData(
      rolePermissionsListQueryOptions(params.id, deps),
    );
    try {
      await queryClient.ensureQueryData(roleQueryOptions(params.id));
    } catch (e) {
      if (!(e instanceof HTTPError)) throw e;
      if (e.response.status == 404) throw notFound();
    }
  },
  staticData: {
    permissionGuards: [
      {
        resource: "permissions",
        actions: ["manage"],
      },
    ],
  },
});

const columns: Array<ColumnDef<RolePermissionAssociation>> = [
  {
    header: "Name",
    accessorKey: "permission.name",
    size: 60,
  },
  {
    header: "Resource",
    accessorKey: "permission.resource",
    size: 60,
    cell: (info) => <Badge variant="outline">{info.getValue<string>()}</Badge>,
  },
  {
    header: "Permissions",
    accessorKey: "permission.action",
    size: 60,
    cell: (info) => (
      <Badge variant="secondary">{info.getValue<string>()}</Badge>
    ),
  },
  {
    header: "Description",
    accessorKey: "permission.description",
    size: 60,
  },
  {
    id: "actions",
    header: "Actions",
    size: 60,
    // meta: { align: "right" },
    cell: ({ row: { original } }) => {
      return (
        <DeleteDialog
          permission={original.permission}
          roleId={original.role_id}
        />
      );
    },
  },
];

function RouteComponent() {
  const { id: roleId } = Route.useParams();
  const searchParams = Route.useSearch();

  const rolePerms = useRolePermissionsList(roleId, {
    page: searchParams.page,
    per_page: searchParams.per_page,
  });

  const table = useReactTable({
    data: rolePerms.data?.items ?? [],
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      pagination: {
        pageIndex: searchParams.page - 1,
        pageSize: searchParams.per_page,
      },
    },
    rowCount: rolePerms.data?.count ?? 1,

    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <BreadcrumbNav
          items={[
            {
              label: "Roles",
              link: (
                <Link to="/roles" search={{ page: 1, per_page: 10 }}>
                  Roles
                </Link>
              ),
            },
            { label: "Role Permissions" },
          ]}
        />
        <AddPermissionsDialog
          roleId={roleId}
          existingPermissionIds={
            rolePerms.data?.items.map((a) => a.permission_id) ?? []
          }
        />
      </div>

      <div className="rounded-md border">
        <GenericTable table={table} isLoading={rolePerms.isLoading} />
      </div>

      <div className="mt-4">
        <GenericPaginationLinks table={table} />
      </div>
    </div>
  );
}

function AddPermissionsDialog({
  roleId,
  existingPermissionIds,
}: {
  roleId: number;
  existingPermissionIds: Array<number>;
}) {
  const [open, setOpen] = React.useState(false);

  const [searchQuery, setSearchQuery] = React.useState("");

  // TODO: switch back to paginated endpoint when proper search is implemented
  const permissions = useAllPermissions();

  const queryClient = useQueryClient();
  const bulkAddMutation = useMutation({
    mutationFn: async ({
      roleId,
      permission_ids,
    }: {
      roleId: number;
      permission_ids: Array<number>;
    }) => {
      await api.raw.post(`api/v1/roles/${roleId}/permissions/bulk`, {
        json: { permission_ids },
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["roles", roleId, "permissions", "list"],
      });

      toast.success(
        `${variables.permission_ids.length} permissions added successfully`,
      );
      setOpen(false);
      selectedPermissions.clear();
    },
  });

  const existingPermissionIdsSet = React.useMemo(
    () => new Set(existingPermissionIds),
    [existingPermissionIds],
  );

  const selectedPermissions = useSet<number>();

  const addPermsColumns = React.useMemo(
    () =>
      [
        {
          id: "select",
          header: ({ table }) => {
            // Count how many non-existing permissions are selected
            const selectableRows = table
              .getRowModel()
              .rows.filter((r) => !existingPermissionIdsSet.has(r.original.id));
            const selectedSelectableCount = selectableRows.filter((r) =>
              selectedPermissions.has(r.original.id),
            ).length;

            const isAllSelected =
              selectableRows.length > 0 &&
              selectedSelectableCount === selectableRows.length;
            const isSomeSelected =
              selectedSelectableCount > 0 &&
              selectedSelectableCount < selectableRows.length;

            return (
              <Checkbox
                checked={
                  isAllSelected
                    ? true
                    : isSomeSelected
                      ? "indeterminate"
                      : false
                }
                onCheckedChange={(value) => {
                  if (value) {
                    // Add all selectable (non-existing) permissions
                    selectableRows.forEach((r) =>
                      selectedPermissions.add(r.original.id),
                    );
                  } else {
                    selectedPermissions.clear();
                  }
                }}
                aria-label="Select all"
              />
            );
          },
          cell: ({ row }) => {
            const isExisting = existingPermissionIdsSet.has(row.original.id);
            const isChecked =
              isExisting || selectedPermissions.has(row.original.id);

            return (
              <Checkbox
                checked={isChecked}
                disabled={isExisting}
                onCheckedChange={(value) => {
                  if (isExisting) return;
                  if (value) {
                    selectedPermissions.add(row.original.id);
                  } else {
                    selectedPermissions.delete(row.original.id);
                  }
                }}
                aria-label="Select row"
              />
            );
          },
        },
        {
          accessorKey: "name",
          header: "Name",
          cell: ({ row, getValue }) => {
            const isExisting = existingPermissionIdsSet.has(row.original.id);
            return (
              <div className="flex items-center gap-2">
                <span>{getValue<string>()}</span>
                {isExisting && (
                  <Badge variant="secondary" className="text-xs">
                    Already Added
                  </Badge>
                )}
              </div>
            );
          },
        },
        {
          accessorKey: "resource",
          header: "Resource",
          cell: (info) => (
            <Badge variant="outline">{info.getValue<string>()}</Badge>
          ),
        },
        {
          accessorKey: "action",
          header: "Action",
          cell: (info) => (
            <Badge variant="secondary">{info.getValue<string>()}</Badge>
          ),
        },
        {
          accessorKey: "description",
          header: "Description",
        },
      ] as const satisfies Array<ColumnDef<Permission>>,
    [roleId, existingPermissionIdsSet, selectedPermissions],
  );

  const table = useReactTable({
    data: permissions.data ?? [],
    columns: addPermsColumns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      globalFilter: searchQuery,
    },
    onGlobalFilterChange: setSearchQuery,
    globalFilterFn: "fuzzy",

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const newSelectionCount = selectedPermissions.size;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusIcon />
          Add Permissions
        </Button>
      </DialogTrigger>
      <DialogContent className="flex max-h-9/10 flex-col overflow-clip sm:max-w-8/10">
        <DialogHeader>
          <DialogTitle>Add Permissions</DialogTitle>
          <DialogDescription>
            Select permissions to add to this role. ({newSelectionCount} new
            permission(s) selected.)
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          <div className="mb-4">
            <Input
              placeholder="Search permissions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-sm"
            />
          </div>

          <div className="rounded-md border">
            <GenericTable table={table} isLoading={permissions.isLoading} />
          </div>

          <div className="mt-4">
            <Pagination className="py-3">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPreviousButton
                    onClick={table.previousPage}
                    disabled={!table.getCanPreviousPage()}
                    className={
                      !table.getCanPreviousPage()
                        ? "cursor-not-allowed opacity-50"
                        : undefined
                    }
                  />
                </PaginationItem>

                <PaginationItem>
                  <span className="px-3 text-sm tabular-nums">
                    Page {table.getState().pagination.pageIndex + 1} of{" "}
                    {table.getPageCount() || 1}
                  </span>
                </PaginationItem>

                <PaginationItem>
                  <PaginationNextButton
                    onClick={table.nextPage}
                    disabled={!table.getCanNextPage()}
                    className={
                      !table.getCanNextPage()
                        ? "cursor-not-allowed opacity-50"
                        : undefined
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t pt-4">
          <Button
            variant="outline"
            onClick={() => {
              selectedPermissions.clear();
              setOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() =>
              bulkAddMutation.mutate({
                roleId,
                permission_ids: [...selectedPermissions],
              })
            }
            disabled={newSelectionCount === 0 || bulkAddMutation.isPending}
          >
            {bulkAddMutation.isPending
              ? "Adding..."
              : `Add ${newSelectionCount} Permission(s)`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({
  permission,
  roleId,
}: {
  permission: Permission;
  roleId: number;
}) {
  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      roleId,
      permissionId,
    }: {
      roleId: number;
      permissionId: number;
    }) => {
      await api.raw.delete(
        `api/v1/roles/${roleId}/permissions/${permissionId}`,
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["roles", variables.roleId, "permissions", "list"],
      });
      setOpen(false);
      toast.success(`Permission "${permission.name}" deleted successfully`);
    },
  });

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant="outline">
          <TrashIcon className="size-4" />
          Delete
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="sm:max-w-xs p-4 gap-3">
        <AlertDialogHeader>
          <AlertDialogTitle>Remove Permission</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to remove the permission "{permission.name}"?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={mutation.isPending} asChild>
            <Button variant="outline">Cancel</Button>
          </AlertDialogCancel>

          <Button
            variant="destructive"
            disabled={mutation.isPending}
            onClick={() =>
              mutation.mutate({ permissionId: permission.id, roleId })
            }
          >
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
